import partition from 'lodash/partition';

import { isFeatureEnabled } from '@lumapps/features';
import { createSelector } from '@lumapps/redux/reselect';
import { FrontOfficeStore } from '@lumapps/redux/types';
import { WIDGET_TITLE_TYPE } from '@lumapps/widget-title/constants';

import { CLIENT_COMPUTED_WIDGET_TYPE, TRANSLATABLE_WIDGET_TYPES } from '../constants';
import { domain } from './domain';
import { WidgetState } from './type';

/** Get widget v2 redux state in store. */
export const getWidgetState = (store: FrontOfficeStore) => store[domain];

export const getWidgetEntities = createSelector(getWidgetState, (state) => state.entities);

/** Get widget state by `widgetId` in store. */
export const getWidget = (state: FrontOfficeStore, { widgetId }: { widgetId?: string }) => {
    if (!widgetId) {
        return undefined;
    }
    return getWidgetState(state).entities[widgetId];
};

export const getWidgetsOfType = (widgetType?: string) => (state: FrontOfficeStore) => {
    if (!widgetType) {
        return undefined;
    }
    const widgets = Object.values(getWidgetState(state)?.entities || {});
    return widgets.filter((widget) => widget.widgetType === widgetType);
};

export const getClientComputedWidgetsOfType = (widgetType?: string) => (state: FrontOfficeStore) => {
    if (!widgetType) {
        return undefined;
    }

    return getWidgetsOfType(CLIENT_COMPUTED_WIDGET_TYPE)(state)?.filter(
        (widget) => widget.body?.widgetType === widgetType,
    );
};

export const getWidgetLoadingState = (state: FrontOfficeStore, { widgetId }: { widgetId?: string }) => {
    return getWidget(state, { widgetId })?.state;
};

export const getWidgetPaginationType = (state: FrontOfficeStore, { widgetId }: { widgetId?: string }) => {
    return getWidget(state, { widgetId })?.paginationType;
};

export const getTranslatableWidgets = createSelector(getWidgetEntities, (entities) => {
    const widgets = Object.values(entities);
    const translatableWidgets = widgets.filter((widget) => TRANSLATABLE_WIDGET_TYPES.includes(widget.widgetType));

    if (!translatableWidgets.length) {
        return null;
    }

    // Split title widgets from other translatable widgets
    const [titleWidgets, otherWidgets] = partition(
        translatableWidgets,
        (widget) => widget.widgetType === WIDGET_TITLE_TYPE,
    );

    return { titleWidgets, otherWidgets };
});

export const getTranslationEntity = (
    state: FrontOfficeStore,
    { widgetId }: { widgetId: string },
): Partial<WidgetState> => {
    return getWidgetState(state).translationEntities[widgetId];
};

export const isNGIContentFiltersFFEnabled = isFeatureEnabled('ngi-content-filter');

export const isNGIContentFiltersGlobalSearchFFEnabled = isFeatureEnabled('ngi-content-filter-global-search-link');

export const getMainWidget = createSelector(getWidgetEntities, (entities) =>
    Object.values(entities).find((entity) => entity.isMainWidget),
);
